<template>
  <div v-if="showScores">
    <div class="score-averages">
      <div ref="scoreAveragesCategories" class="score-averages__categories">
        <!-- category select desktop --->
        <div v-for="category in categories"
             :key="category"
             :class="{ 'active': current_category_name === category }"
             class="category-web"
             @click="selectCategory(category,$event)">
          {{ $t(category) }}
        </div>

        <!-- category select mobile --->
        <el-select v-model="current_category_name"
                   :placeholder="$t('Overall')"
                   default-first-option
                   class="category-select-mobile"
                   @change="newSelectCategory">
          <el-option v-for="category in categories"
                     :key="category"
                     :value="category"
                     class="category-option">
            <span>{{ $t(category) }}</span>
            <i v-show="category === current_category_name" class="mdi mdi-check check" />
          </el-option>
        </el-select>
      </div>

      <div ref="scoreAveragesWrapper"
           class="scores-wrapper"
           :class="{'with-category-dropdown': categories.length > 2}">
        <div class="score-averages__list">
          <HorizontalLoading v-show="isLoading" />
          <el-card
            v-for="score in isMobile ? mobileCurrentCategoryScores : currentCategoryScores"
            :key="score.name">
            <div
              :class="scoreClasses(score.name)"
              @click="focusScore(score.name)"
              @mouseover="highlightScore(score.name)"
              @mouseout="unHighlightScore(score.name)">
              <span class="name"> {{ normalizeString(score.name) }} </span>
              <div v-if="score.isNil" class="value">
                -
              </div>
              <div v-else-if="score.isBoolean" class="value">
                <Status :status="score.rawScore" />
              </div>
              <div v-else-if="score.isPlain" class="value">
                {{ score.fixedScore }}
              </div>
              <div v-else-if="score.isRounded" class="value">
                {{ score.roundedScore }}
              </div>
              <div v-else-if="score.isPercentage" class="value">
                {{ score.percentageScore }}
              </div>
              <div v-else-if="score.isFraction" class="value">
                {{ score.fractionScore }}
              </div>
              <div v-else-if="score.isStatus" class="value">
                <Status :status="score.rawScore" />
              </div>
              <div v-else-if="score.isString" class="string-value">
                {{ normalizeString(score.rawScore) }}
              </div>
            </div>
          </el-card>
        </div>
        <span v-if="isMobile && currentCategoryScores.length > 4" class="expand-button-wrapper">
          <i v-if="!cardsExpanded"
             class="expand-button pointer-cursor el-icon-plus"
             @click="cardsExpanded = true" />
          <i v-else
             class="expand-button pointer-cursor el-icon-minus"
             @click="cardsExpanded = false" />
          <span v-if="!cardsExpanded"
                class="expand-text"
                @click="cardsExpanded = true">
            {{ $t(`ALL KPI's`) }} <i class="el-icon-plus" />
          </span>
          <span v-else
                class="expand-text"
                @click="cardsExpanded = false">
            {{ $t(`ALL KPI's`) }} <i class="el-icon-minus" />
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { chain } from 'lodash';
import { mapGetters } from 'vuex';
import mobileScreenControl from '@/helpers/mixins/mobileScreenControl';
import scoreCalculator from '@/helpers/scoreCalculator';
import kpiCardHighlight from '@/helpers/mixins/kpiCardHighlight';

export default {
  name: 'ScoreAverages',
  mixins: [mobileScreenControl, kpiCardHighlight],
  props: {
    reportData: {
      type: Object,
      required: true
    },
    type: {
      type: String,
      default: 'visit'
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    scoreIndicator: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      current_category_name: 'Overall',
      cardsExpanded: false
    };
  },
  computed: {
    ...mapGetters(['scoreTemplates']),
    numberOfShowedCards() {
      if (this.screenWidth < 641) {
        return 3;
      }

      return 4;
    },
    categories() {
      return chain(this.reportData)
        .result('report.filtered_scores.category', {})
        .keys()
        .sort()
        .unshift('Overall')
        .value();
    },
    categoryScores() {
      if (!this.filteredScoreTemplates.length) return [];

      return this.categories.map(category => ({
        category_name: category,
        scores: this.filteredScoreTemplates.map(template => this.getScore(template, category))
      }));
    },
    filteredScoreTemplates() {
      if (this.type === 'visit_index') {
        return chain(this.scoreTemplates).filter(t => t.show_visit_index).sortBy(t => t.position).value();
      }

      if (this.type === 'team') {
        return chain(this.scoreTemplates).filter(t => t.show_team_tab).sortBy(t => t.position).value();
      }

      if (this.type === 'store') {
        return chain(this.scoreTemplates).filter(t => t.show_store_tab).sortBy(t => t.position).value();
      }

      return chain(this.scoreTemplates).filter(t => t.show_auditor_report).sortBy(t => t.position).value();
    },
    showScores() {
      return this.filteredScoreTemplates.length;
    },
    currentCategoryScores() {
      const list = this.categoryScores.find(score => score.category_name === this.current_category_name);
      const filteredScores = [];

      list.scores.forEach((score) => {
        const s = this.filteredScoreTemplates.find(fs => fs.name === score.name);

        if (s.categories && s.categories[this.current_category_name] === false) return;

        filteredScores.push(score);
      });

      return filteredScores;
    },
    mobileCurrentCategoryScores() {
      return this.currentCategoryScores.slice(0, this.cardsExpanded ? this.currentCategoryScores.length : this.numberOfShowedCards);
    }
  },
  watch: {
    scoreIndicator() {
      this.current_category_name = this.scoreIndicator;
    },
    current_category_name(item) {
      this.$emit('selectScoreCategory', item);
    }
  },
  mounted() {
    // Adjusting the height of 'scoreAveragesCategories' to match 'scoreAveragesWrapper' for consistency.
    const { scoreAveragesWrapper, scoreAveragesCategories } = this.$refs;
    const elementHeight = scoreAveragesWrapper.offsetHeight;

    scoreAveragesCategories.style.height = `${elementHeight}px`;
  },
  methods: {
    selectCategory(category, event) {
      this.current_category_name = category;
      event.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    newSelectCategory(category) {
      this.current_category_name = category;
    },
    getScore(template, category) {
      const re = new RegExp(/.*(?=\.[^.]*$)/gi);

      const scorePath = category === 'Overall' ? `report.${template.value_path.match(re)[0]}.${template.name}` : `report.filtered_scores.category.${category}.${template.name}`;

      return scoreCalculator(this.reportData, {
        ...template,
        value_path: scorePath
      });
    }
  }

};
</script>

<style lang="scss">
.score-averages {
  display: flex;
  align-items: flex-start;
  border-radius: 10px;

  @include breakpoint(lg-max) {
    flex-direction: column;
    background: transparent;

    .expand-button {
      margin-right: 8px;
      font-size: 28px;
      font-weight: 900;
      color: $vispera-blue;
    }

    .expand-text {
      display: none;
    }
  }

  @include breakpoint(md-s-max) {
    .expand-button {
      display: none;
    }

    .expand-text {
      display: inherit;
      font-size: 12px;
      font-weight: 700;
      color: $vispera-blue;

      i {
        font-size: 12px;
        font-weight: 800;
      }
    }
  }

  .expand-button-wrapper {
    align-self: flex-start;
    margin-top: 20px;

    @include breakpoint(md-s-max) {
      align-self: center;
      margin-top: 5px;
    }
  }

  .scores-wrapper {
    display: flex;
    width: 100%;

    @include breakpoint(lg-max) {
      &.with-category-dropdown {
        margin-top: 0;
      }
    }

    @include breakpoint(md-s-max) {
      flex-direction: column;
      margin-top: 0;
    }
  }

  &__categories {
    box-sizing: border-box;
    flex: 0 0 150px;
    align-self: flex-start;
    min-width: 200px;
    min-height: 130px;
    padding: 5px;
    margin-right: 20px;
    overflow-y: scroll;

    @include breakpoint(lg-max) {
      flex: 0;
      min-height: unset !important;
    }

    @include breakpoint(md-s-max) {
      width: 100%;
    }

    .category-web {
      padding: 4px 4px 4px 10px;
      font-size: 12px;
      font-weight: bold;
      cursor: pointer;

      @include breakpoint(lg-max) {
        display: none;
      }

      &.active {
        color: $white;
        background: $tooGold;
        border-radius: 10px;
      }
    }

    .category-select-mobile {
      display: none;

      @include breakpoint(lg-max) {
        display: inherit;

        .el-input {
          &__inner {
            height: 30px;
            font-size: 13px;
            color: $white;
            background-color: #f9b723;
            border-radius: 130px;

            &::placeholder {
              color: $white;
            }
          }

          &__suffix {
            display: flex;
            align-items: center;

            &-inner {
              i {
                font-size: 13px;
                font-weight: bold;
                color: $white;
              }
            }
          }
        }
      }
    }
  }

  &-categories::-webkit-scrollbar {
    width: 3px !important;
    -webkit-appearance: none;
  }

  &-categories::-webkit-scrollbar-thumb {
    background: $shutterBlue !important;
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
  }

  &__list {
    position: relative;
    display: grid;
    grid-template-rows: auto;
    grid-template-columns: repeat(3, 1fr);
    width: 100%;

    @include breakpoint(lg-max) {
      grid-template-columns: repeat(4, minmax(136px, 1fr));
    }

    @include breakpoint(md-s-max) {
      grid-template-columns: repeat(1, 1fr);
    }

    .el-card {
      display: flex;
      margin: 5px;
      border: 0;
      box-shadow: unset !important;

      &__body {
        width: 100%;
        padding: 0 !important;
      }

      &__wrapper {
        box-sizing: border-box;
        display: flex;
        flex: 1;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        height: 100%;
        padding: 15px;
        cursor: pointer;

        &--highlighted {
          background: #e8e8e8;
        }

        @include breakpoint(lg-max) {
          padding: 10px;
        }

        .name {
          flex: 1;
          margin-right: 15px;
          font-size: 16px;
          font-weight: 600;
          line-height: 24px;
          color: #666;

          @include breakpoint(lg-max) {
            font-size: 13px;
            line-height: 13px;
          }

          @include breakpoint(md-s-max) {
            font-size: 16px;
          }
        }

        .value {
          font-size: 22px;
          font-weight: 600;
          color: #666;
        }

        .string-value {
          font-size: 14px;
        }
      }
    }
  }
}

@include breakpoint(lg-max) {
  .category-option {
    position: relative;
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  .el-select-dropdown {
    padding-bottom: 8px;
  }
}
</style>
